import {Component, OnInit, Input, OnChanges} from '@angular/core';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {SafeHtml} from '@angular/platform-browser';
import {debounceTime} from 'rxjs/operators';
import {ValidationPartialComponent} from '../partials';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {NgIf, NgFor} from '@angular/common';
import {MatCardModule} from '@angular/material/card';
import {DataHandlerService} from '../../../services/data-handler.service';
import {SalesService, SubmissionDefaults, SubmissionService} from '../../../services';
import * as _ from 'lodash';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatProgressBar} from '@angular/material/progress-bar';

@Component({
  selector: 'app-submit',
  templateUrl: './submit.component.html',
  standalone: true,
  imports: [MatCardModule, FormsModule, ReactiveFormsModule, NgIf, MatCheckboxModule,
    MatFormFieldModule, ValidationPartialComponent, NgFor, MatProgressSpinner, MatProgressBar]
})
export class SubmitComponent implements OnInit, OnChanges {
  @Input() public personList: UntypedFormArray;
  @Input() public formGroup: UntypedFormGroup;
  @Input() public baseform: any;
  @Input() public generalContent: SafeHtml;
  @Input() public isInternalUser = false;
  @Input() public isHomeLoan = false;
  @Input() public ndiTrustCompanyResultsDisabled = true;
  @Input() public loanPurposeType;
  @Input() public storageIdentifier;
  @Input() public salesRelatesTo;
  @Input() public activeStep: any;
  @Input() public steps: any;
  @Input() public applicationType: string;

  public ndiStatusClassName = 'error-block';
  public ndiResultsEnabled = true;
  public runningCalculations = false;
  public ndiPass = false;
  public pndiPass = true;
  public ndiScore: any = 'N/A';
  public isNasApplicationType = false;
  public NAS_APPLICATION_TYPE = 'individual-with-non-applicant';

  constructor(public salesService: SalesService,
              public submissionService: SubmissionService,
              public dataHandlerService: DataHandlerService) {
  }

  ngOnInit() {
    this.checkPrivacyFormControl();

    this.personList.valueChanges.pipe(debounceTime(2000))
      .subscribe((value) => {
        this.checkSubmissionValidation();
      });

    this.dataHandlerService.getMainBorrowerType()
      .subscribe((value) => {
        this.isNasApplicationType = (value === this.NAS_APPLICATION_TYPE);
        console.log('isNasApplicationType', this.isNasApplicationType);
      });
  }

  ngOnChanges(changes) {
    if (changes.activeStep && changes.activeStep.currentValue && this.steps[changes.activeStep.currentValue]?.widgets.find(x => x.name === 'app-submit')) {
      this.getNdiScore();
    }

    if (changes && changes.ndiTrustCompanyResultsDisabled && changes.ndiTrustCompanyResultsDisabled.currentValue !== changes.ndiTrustCompanyResultsDisabled.previousValue) {
      this.checkNdiEnabled();
    }

    if (changes && changes.applicationType && changes.applicationType.currentValue !== changes.applicationType.previousValue && !changes.applicationType.firstChange) {
      this.checkNdiEnabled();
    }
  }

  checkNdiEnabled() {
    console.log('this.applicationType', this.applicationType);
    if (this.isHomeLoan && this.ndiTrustCompanyResultsDisabled && this.applicationType && this.applicationType !== 'new-personal' && this.applicationType !== 'new-bridging') {
      console.log('disable ndi results');
      // don't show the results and set ndi as pass so that smsf, company and trust applications avoid validation on broker notes
      this.ndiResultsEnabled = false;
      this.dataHandlerService.setNdiPassFlag(true);
    } else {
      this.ndiResultsEnabled = true;
    }
  }

  async getNdiScore() {
    if (this.ndiResultsEnabled) {
      // ensure the broker does not process until calculations have completed
      this.dataHandlerService.setNdiPassFlag(false);
      this.dataHandlerService.setPndiPassFlag(false);
      this.runningCalculations = true;

      const data: any = this.convertToSubmissionPayload(this.baseform.getRawValue(), this.loanPurposeType, this.storageIdentifier, this.salesRelatesTo);
      this.salesService.runCalculation('pre-qualify', data, 100)
        .subscribe((result: any) => {
          this.runningCalculations = false;
          console.log('prequalify result', result);
          if (result && result.ndiRatio) {
            this.ndiScore = result.ndiRatio;
            this.ndiPass = (result.ndiRatio >= 1);
            this.pndiPass = (result.pNDIRatio == null) ? true : (result.pNDIRatio >= 1);
            this.ndiStatusClassName = (result.ndiRatio >= 1) ? 'success-block' : 'error-block';

            this.dataHandlerService.setNdiFlags({pndi: this.pndiPass, ndi: this.ndiPass});
            this.dataHandlerService.setNdiPassFlag(this.ndiPass);
            this.dataHandlerService.setPndiPassFlag(this.pndiPass);
          }
        });
    }
  }

  convertToSubmissionPayload(data, loanPurposeType, storageIdentifier, salesRelatesTo) {
    let lead;
    try {
      lead = JSON.parse(sessionStorage.getItem(storageIdentifier + '-lead'));
    } catch {
      console.log('Unable to parse lead from session storage: ' + sessionStorage.getItem(storageIdentifier + '-lead'));
      lead = null;
    }

    const defaults: SubmissionDefaults = {
      purposeType: loanPurposeType,
      salesRelatesTo: salesRelatesTo,
      shop: (lead && lead.shop) ? lead.shop : null,
      channel: (lead && lead.channel) ? lead.channel : null,
    };

    return this.submissionService.parse(data, defaults);
  }

  checkPrivacyFormControl() {
    const privacyConsent = true;

    if (this.personList && this.personList.controls && this.personList.controls.length > 0) {
      this.personList.controls.forEach((formGroup: any) => {
        if (!formGroup.get('privacyConsent')) {
          formGroup.addControl('privacyConsent', new UntypedFormControl(privacyConsent));
        } else {
          formGroup.get('privacyConsent').patchValue(privacyConsent);
        }
      });
    }
  }

  checkSubmissionValidation() {
    const rawPersonList = this.personList.getRawValue();
    const foundConsent = rawPersonList.filter(x => x.privacyConsent);

    console.log('checkSubmissionValidation', foundConsent);

    if (foundConsent && foundConsent.length > 0) {
      this.formGroup.get('generalTerms').patchValue(true);
    } else {
      this.formGroup.get('generalTerms').patchValue(false);
    }
  }

  checkGeneralTerms(ev) {
    const consent = !!(ev.checked);
    this.personList.controls.forEach((formGroup: any) => {
      if (!formGroup.get('privacyConsent')) {
        formGroup.addControl('privacyConsent', new UntypedFormControl(true));
      } else {
        formGroup.get('privacyConsent').patchValue(consent, {emitEvent: false});
      }
    });
  }
}
